// Modo "Carrusel" — pantalla propia (ruta /carrusel).
//
// Promueve a modo la función que hasta ahora sólo existía como un botón adentro
// del historial (GeneratedImagesHistory) y del visor inline (GeneratedImagesInline),
// donde no la encontraba nadie que no abriera una imagen generada. Suma además un
// caso nuevo: partir de una FOTO PROPIA, no sólo de lo que generó Atlas — para eso
// el backend aprendió a aceptar una referencia de storage además del id de fila
// (ver ADR-2026-08-14-modo-carrusel).
//
// UI: calcada de AnimarImagenMode, que es el otro modo que arranca de una imagen:
//   • desktop → el layout de los modos del rediseño (`ShellModoRedisenado`):
//     breadcrumb + título adentro del panel de la IZQUIERDA, que es donde se elige
//     la imagen, y la muestra a la derecha. En el paso 2 la imagen elegida encabeza
//     la configuración como card y se cambia en el panel lateral, sin salir de ella
//     (ADR-2026-09-07-carrusel-toma-el-diseno-de-los-modos).
//   • mobile  → carátula full-bleed arriba y panel blanco redondeado abajo
//     (-mt-6 rounded-t-3xl), con el back flotante del shell (mobileBackOverride,
//     que además esconde la bottom nav). Sin cambios.
//
// Dos pantallas: (1) elegir la imagen — subir foto o elegir una generada;
// (2) elegir cuántas variaciones y generar. Al generar: pantalla de carga estándar
// → la galería de resultados, igual que el botón de siempre. La foto subida NO se
// registra en `imagenes_generadas` (ver lib/animarImagenUpload).
//
// OJO con los colores: las clases token-con-opacidad (`text-brand-black/90`,
// `bg-brand-primary/10`) NO compilan en este Tailwind — quedan sin regla en el CSS.
// Por eso acá se usa `text-brand-black` pelado y hex arbitrario con alpha
// (`text-[#3C3C43]/60`, `bg-[#1644EB]/10`), que sí compilan.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ImageUp,
  Images,
  Info,
  Loader2,
  Minus,
  Plus,
  Sparkles,
} from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useRegistrarPantalla } from '../../../chat-atlas/PuenteDePantallaContext';
import type { ControladorDePantalla } from '../../../chat-atlas/puenteDePantalla';
import { describirCarrusel } from '../puente/camposDeModo';
import { empresaRecordada, recordarEmpresa } from '../empresaDeLaSesion';
import { useSidebar } from '../../../context/SidebarContext';
import { useVolverAtras } from '../../../hooks/useVolverAtras';
// La vuelta atrás del modo viaja en la entrada de historial, no en `sessionStorage`
// (ADR-2026-09-10-los-modos-con-ruta-propia-tambien-vuelven-a-lo-que-generaste).
import {
  configDelModoAlMontar,
  recordarConfigDelModo,
} from './shared/volverAlModo';
import CachedImg from '../../../components/CachedImg';
import LoadingAnimation from '../../../components/LoadingAnimation';
import SubscriptionLimitModal from '../../../components/SubscriptionLimitModal';
import OptionSheet from '../../../components/opciones/OptionSheet';
import { PasoUnoMobile } from './shared/PasoUnoMobile';
import { CreditBadge } from '../../../components/CreditBadge';
import { GenerateAdError } from '../../../lib/generateAdRequest';
import { subirImagenParaAnimar } from '../../../lib/animarImagenUpload';
import {
  ANGLES_SUBIDA_REQUIERE_ATLAS,
  postGenerateAnglesFrom,
  type AnglesSource,
} from '../../../lib/generateAngles';
import {
  GeneradasPickGrid,
  type GeneradaPick,
} from './shared/GeneradasPickGrid';
import { ShellModoRedisenado } from './shared/ShellModoRedisenado';
import { useColumnaResultadosModo } from './shared/useColumnaResultadosModo';
import { CardPicker } from '../components/PanelCatalogoCompacto';
import { PanelLateralModo } from '../components/PanelLateralModo';
import { nombreCortoModo } from '../modesCatalog';
import { MAX_TANDAS_EN_CURSO } from '../generacionesEnCurso';

/** Carátula del modo — PLACEHOLDER: la misma ilustración que usa el menú viejo
 *  (CarruselMenu). Todavía no hay un clip propio del modo. */
const HERO_IMG = '/images/carrusel.png';

/** Título y bajada del hero, ARRIBA de la carátula — el mismo tratamiento de Modelo v2
 *  y Catálogo (ADR-2026-09-08-la-muestra-de-los-atajos-encabeza-con-titulo). */
const HERO_TITULO = 'Una foto, varias tomas';
const HERO_DESC =
  'Atlas genera nuevos ángulos, poses y encuadres del mismo producto en la misma escena.';

/** Cuántas variaciones pedir. El tope es 6 porque es lo que clampea el backend
 *  (`Math.min(6, …)` en el handler de nuevos ángulos): ofrecer más sería prometer
 *  algo que se recorta en silencio. El mínimo es 2 — un carrusel de una no es un
 *  carrusel, igual que en CarruselMenu. */
const CANTIDAD_MIN = 2;
const CANTIDAD_MAX = 6;

/** La ruta del modo — la entrada de historial que se reemplaza al irse a los resultados. */
const RUTA_MODO = '/carrusel';

/** Lo que hace falta para reabrir el modo en el paso 2, tal como se generó. */
interface ConfigDeCarrusel {
  source: AnglesSource;
  /** La firmada con la que se dibuja el preview. Se guarda en vez de re-firmar: el
   *  "Atrás" pasa segundos después y la URL sigue viva (TTL de una hora). */
  previewUrl: string | null;
  cantidad: number;
}

/** Estimación para la barra de la pantalla de carga: el backend espera hasta 200s
 *  (prompt del carrusel ~25s + las N generaciones en paralelo). */
const CARGA_MS = 180_000;

/** Cuántas veces se pollean las filas nuevas antes de mandar a la galería igual. */
const POLL_INTENTOS = 20;
const POLL_INTERVALO_MS = 1500;

const GENERADAS_BUCKET = 'generadas';
const SIGNED_TTL = 3600;

interface PreloadedImage {
  id: string;
  fileName: string;
  signedUrl: string;
  favorito: boolean;
}

export default function CarruselMode() {
  const navigate = useNavigate();
  const { sidebarWidth, setMobileBackOverride } = useSidebar();
  const volverAtras = useVolverAtras();
  /**
   * La empresa arranca de la que ya se resolvió en esta pestaña y no en `null` —
   * ADR-2026-09-08-la-portada-no-parpadea-mientras-se-resuelve-la-empresa.
   *
   * Este modo tiene ruta propia, así que al entrar monta de cero y pide su `id_company`
   * con `auth.getUser()` + `profiles`: medio segundo largo en el que la columna de
   * resultados no sabe de quién son las tandas y dibuja esqueletos en lugar de lo que se
   * está generando. `empresaRecordada()` lo devuelve al instante; el efecto de abajo lo
   * confirma igual contra la base.
   */
  const [companyId, setCompanyId] = useState<string | null>(() => empresaRecordada());
  const [brandId, setBrandId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  /**
   * La configuración con la que se volvió de generar, si el "Atrás" trajo una —
   * ADR-2026-09-10-los-modos-con-ruta-propia-tambien-vuelven-a-lo-que-generaste.
   *
   * Se lee UNA vez, al montar, y siembra los tres estados de abajo: el modo aparece
   * directamente en su paso 2 con la imagen y la cantidad puestas, en vez de mandar a
   * elegir de nuevo la imagen que se acaba de usar.
   */
  const configAlVolver = useRef(configDelModoAlMontar<ConfigDeCarrusel>()).current;

  // ── Pantalla 1: elegir la imagen ──
  const [paso, setPaso] = useState<'elegir' | 'configurar'>(
    configAlVolver ? 'configurar' : 'elegir',
  );
  const [source, setSource] = useState<AnglesSource | null>(
    configAlVolver?.source ?? null,
  );
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    configAlVolver?.previewUrl ?? null,
  );
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sheetGeneradas, setSheetGeneradas] = useState(false);
  /** El panel lateral de desktop, donde se cambia la imagen sin salir de la
   *  configuración. El mismo mecanismo de Animar, Fotos de producto y Catálogo. */
  const [panelImagenAbierto, setPanelImagenAbierto] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  /**
   * ¿Queda grilla abajo del scroll? Prende el degradado del pie del panel (desktop).
   *
   * Se mide en cada scroll y cada vez que CRECE el contenido: las filas las carga
   * `GeneradasPickGrid` en SU estado, así que este componente no re-renderiza cuando
   * llegan y una medición atada al render se quedaría con la grilla vacía.
   */
  const scrollElegirRef = useRef<HTMLDivElement | null>(null);
  const contenidoElegirRef = useRef<HTMLDivElement | null>(null);
  const [quedaContenidoAbajo, setQuedaContenidoAbajo] = useState(false);
  const medirFinDelScroll = (el: HTMLDivElement | null) => {
    if (!el) return;
    // 4px de tolerancia: con zoom del browser `scrollTop` queda fraccionario y la
    // resta nunca da exactamente 0 al final.
    setQuedaContenidoAbajo(el.scrollHeight - el.scrollTop - el.clientHeight > 4);
  };
  useEffect(() => {
    const contenido = contenidoElegirRef.current;
    if (!contenido) return;
    const medir = () => medirFinDelScroll(scrollElegirRef.current);
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(contenido);
    window.addEventListener('resize', medir);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', medir);
    };
  }, [paso]);
  // Cada selección invalida a las anteriores en vuelo: si una subida vieja termina
  // tarde no puede pisar la imagen que el usuario está mirando.
  const uploadSeqRef = useRef(0);

  // ── Pantalla 2: cuántas variaciones ──
  const [cantidad, setCantidad] = useState(configAlVolver?.cantidad ?? CANTIDAD_MIN);
  const [generando, setGenerando] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [completeNow, setCompleteNow] = useState(false);
  const [limitModal, setLimitModal] = useState<
    'free-trial-ended' | 'plan-limit-reached' | null
  >(null);
  const pendingNavRef = useRef<{
    imageIds: string[];
    companyId: string;
    preloaded: PreloadedImage[];
  } | null>(null);
  /**
   * El plan que devolvió el último guard de créditos. Lo necesita el modal de límite
   * cuando el que avisa es el BACKEND (la promesa rechaza con un error de billing
   * después de que la tanda ya está en la columna): ahí no hay a quién preguntarle el
   * plan sin otra vuelta a Supabase, y el guard lo acaba de leer.
   */
  const planRef = useRef<string | null>(null);

  // 1 crédito por imagen nueva (el backend cobra 1 por hijo generado).
  const costo = cantidad;

  // El chat puede leer y tocar esta pantalla (ADR-2026-09-10-el-agente-toca-la-pantalla):
  // la cantidad de tomas. La imagen se elige en pantalla.
  useRegistrarPantalla(
    useMemo<ControladorDePantalla>(
      () => ({
        clave: 'carrusel',
        describir: () =>
          describirCarrusel({
            ruta: '/carrusel',
            paso,
            imagenElegida: !!source,
            cantidad,
            min: CANTIDAD_MIN,
            max: CANTIDAD_MAX,
          }),
        aplicar: (cambios) => {
          const aplicados: string[] = [];
          for (const c of cambios) {
            if (c.campo === 'cantidad') setCantidad(c.valor as number);
            else continue;
            aplicados.push(c.campo);
          }
          return { aplicados, fallidos: [] };
        },
      }),
      [paso, source, cantidad],
    ),
  );

  useEffect(() => {
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user) return;
      setUserId(auth.user.id);
      const { data: p } = await supabase
        .from('profiles')
        // `current_brand_id` además de la empresa: la columna de resultados lista las
        // creaciones con el MISMO filtro de marca que el historial.
        .select('id_company, current_brand_id')
        .eq('id', auth.user.id)
        .maybeSingle();
      const perfil = p as {
        id_company?: string | null;
        current_brand_id?: string | null;
      } | null;
      setCompanyId(perfil?.id_company ?? null);
      setBrandId(perfil?.current_brand_id ?? null);
      // Que el próximo modo que se abra la tenga sin esperar su propia consulta.
      recordarEmpresa(perfil?.id_company, auth.user.id);
    })();
  }, []);

  const volverAElegir = () => {
    setPaso('elegir');
    setSource(null);
    setPreviewUrl(null);
    setCantidad(CANTIDAD_MIN);
    setError(null);
  };

  // Back flotante del shell en mobile. Además de dar el botón, esconde la bottom
  // nav (MobileBottomNav se apaga con `mobileBackOverride !== null`), que es lo
  // que hacen el resto de los modos al ocupar la pantalla entera.
  useEffect(() => {
    setMobileBackOverride(() => () => {
      if (paso === 'configurar') volverAElegir();
      // El back de la PRIMERA pantalla del modo vuelve a donde estabas —
      // ADR-2026-09-07-el-back-de-los-modos-vuelve-a-donde-estabas. Antes iba fijo a
      // `/modos`: entrando desde la home (que es de donde se entra casi siempre), el
      // back te dejaba en una pantalla por la que no habías pasado.
      else volverAtras();
    });
    return () => setMobileBackOverride(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paso, volverAtras]);

  const elegirArchivo = () => fileInputRef.current?.click();

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permitir re-elegir el mismo archivo
    if (!file) return;
    if (!companyId) {
      setError('No pudimos identificar tu cuenta. Recargá la página.');
      return;
    }
    const seq = ++uploadSeqRef.current;
    setError(null);
    setSubiendo(true);
    try {
      const subida = await subirImagenParaAnimar(file, companyId, 'carrusel');
      if (seq !== uploadSeqRef.current) return; // ganó una selección más nueva
      setSource({ tipo: 'subida', bucket: subida.bucket, path: subida.path });
      setPreviewUrl(subida.previewUrl);
      setPanelImagenAbierto(false);
      setPaso('configurar');
    } catch (err) {
      if (seq !== uploadSeqRef.current) return; // el fallo es de una subida vieja
      setError(err instanceof Error ? err.message : 'No se pudo subir la imagen.');
    } finally {
      if (seq === uploadSeqRef.current) {
        setSubiendo(false);
        // El sheet de mobile se cierra al TERMINAR la subida, con o sin éxito —
        // ADR-2026-09-08-la-subida-de-mobile-cierra-el-sheet. Desde que "Subir imagen" es
        // una celda de esa grilla (y no un botón en la pantalla), quien la dispara está
        // ADENTRO del sheet: sin esto la subida corría, la pantalla de atrás avanzaba al
        // paso 2 y el usuario seguía mirando la misma lista, como si no hubiera pasado
        // nada. Y si falla, el aviso que se dibuja abajo tampoco se veía.
        setSheetGeneradas(false);
      }
    }
  };

  const elegirGenerada = (pick: GeneradaPick) => {
    setSource({ tipo: 'generada', imageId: pick.id });
    setPreviewUrl(pick.signedUrl);
    setSheetGeneradas(false);
    setPanelImagenAbierto(false);
    // La cantidad NO se reinicia: cambiar de imagen desde el panel lateral es cambiar
    // UN dato de la configuración, no empezarla de nuevo.
    setPaso('configurar');
  };

  /** Espera a que las filas nuevas tengan `file_name` y precarga sus URLs firmadas,
   *  para que la galería de resultados no muestre huecos. Mismo criterio que el
   *  botón de carrusel del historial. */
  const precargarNuevas = async (ids: string[], company: string) => {
    const preloaded: PreloadedImage[] = [];
    for (let intento = 1; intento <= POLL_INTENTOS; intento++) {
      for (const imgId of ids) {
        if (preloaded.some((p) => p.id === imgId)) continue;
        try {
          const { data: fila } = await supabase
            .from('imagenes_generadas')
            .select('file_name, favorito')
            .eq('id', imgId)
            .eq('id_company', company)
            .single();
          if (!fila?.file_name) continue;
          const { data: signed } = await supabase.storage
            .from(GENERADAS_BUCKET)
            .createSignedUrl(`${company}/full/${fila.file_name}`, SIGNED_TTL);
          if (!signed?.signedUrl) continue;
          preloaded.push({
            id: imgId,
            fileName: fila.file_name,
            signedUrl: signed.signedUrl,
            favorito: fila.favorito === true,
          });
          const img = new Image();
          img.src = signed.signedUrl; // deja la imagen en el cache del browser
        } catch {
          // fila todavía no visible: se reintenta en la próxima vuelta
        }
      }
      if (preloaded.length === ids.length) break;
      await new Promise((r) => setTimeout(r, POLL_INTERVALO_MS));
    }
    return preloaded;
  };

  /**
   * La COLUMNA DE RESULTADOS del modo — ADR-2026-09-08-animar-y-carrusel-generan-en-la-columna.
   *
   * Las tomas nuevas aterrizan a la derecha, donde estaba la muestra, igual que en
   * Automática, Modelo, Fotos de producto y Catálogo: generar deja de llevarse la
   * pantalla.
   */
  const { columna, hayColumna, visorAbierto, lanzarTanda, puedeCrear } =
    useColumnaResultadosModo({
      companyId,
      brandId,
      modo: 'carrusel',
      onError: setError,
      onLimitePlan: () =>
        setLimitModal(
          planRef.current === 'gratis' ? 'free-trial-ended' : 'plan-limit-reached',
        ),
    });

  const generar = async () => {
    if (!source || !userId || !companyId || generando) return;
    /**
     * En DESKTOP la tanda va a la columna de la derecha y la pantalla no se mueve; abajo
     * de `md` no hay dos columnas (el shell es `hidden md:flex`), así que ahí sigue el
     * camino de siempre: pantalla de carga y la galería de resultados.
     */
    const enPanel =
      typeof window !== 'undefined' &&
      window.matchMedia('(min-width: 768px)').matches;
    setGenerando(true);
    setError(null);
    if (!enPanel) {
      setShowLoading(true);
      setCompleteNow(false);
    }
    try {
      // Guard con el dato fresco de Supabase. El carrusel NO tiene restricción de
      // plan (paridad con el botón del historial): sólo créditos suficientes para
      // la cantidad pedida.
      const { data: perfil } = await supabase
        .from('company_profiles')
        .select('plan, imagenes_restantes:imagenes_disponibles')
        .eq('id_company', companyId)
        .single();
      const restantes = (perfil as { imagenes_restantes?: number | null } | null)
        ?.imagenes_restantes;
      const plan = (perfil as { plan?: string } | null)?.plan;
      planRef.current = plan ?? null;
      if (typeof restantes === 'number' && restantes < costo) {
        setShowLoading(false);
        setGenerando(false);
        setLimitModal(plan === 'gratis' ? 'free-trial-ended' : 'plan-limit-reached');
        return;
      }

      if (enPanel) {
        // El tope se pregunta ANTES de disparar: acá todavía se puede frenar y decirlo,
        // que es distinto de lo que hace la columna con un pedido ya en vuelo
        // (ADR-2026-09-06-tope-de-tres-tandas-en-curso).
        if (!puedeCrear()) {
          setGenerando(false);
          setError(
            `Ya hay ${MAX_TANDAS_EN_CURSO} generaciones en curso. Esperá a que termine alguna para pedir otra.`,
          );
          return;
        }
        // La promesa se entrega SIN esperarla: los recuadros salen ya y el usuario se
        // queda en el modo, con el panel de la izquierda listo para el próximo carrusel.
        // El `file_name` y las URLs los espera `preloadGeneratedImages` adentro de la
        // columna — el `precargarNuevas` de acá abajo es del camino de mobile.
        lanzarTanda({
          total: cantidad,
          resultado: postGenerateAnglesFrom(source, { userId, cantidad }).then(
            (ids) => {
              if (ids.length === 0) {
                throw new Error('el backend no devolvió ninguna imagen');
              }
              return ids;
            },
          ),
          errorGenerico: 'No se pudo generar el carrusel. Intentá nuevamente.',
        });
        setGenerando(false);
        return;
      }

      const ids = await postGenerateAnglesFrom(source, {
        userId,
        cantidad,
      });
      if (ids.length === 0) {
        setShowLoading(false);
        setGenerando(false);
        setError('No se pudo generar el carrusel. Intentá nuevamente.');
        return;
      }

      const preloaded = await precargarNuevas(ids, companyId);
      pendingNavRef.current = { imageIds: ids, companyId, preloaded };
      // La vuelta atrás: el "Atrás" desde los resultados devuelve ESTA configuración, no
      // la pantalla de elegir imagen (ADR-2026-09-10-los-modos-con-ruta-propia-tambien-vuelven-a-lo-que-generaste).
      // Se deja antes de navegar, con lo que se generó de verdad.
      recordarConfigDelModo<ConfigDeCarrusel>(navigate, RUTA_MODO, {
        source,
        previewUrl,
        cantidad,
      });
      setCompleteNow(true); // cierra la barra de carga → onCompleted navega
    } catch (e) {
      console.error('[CarruselMode] generar falló', e);
      setShowLoading(false);
      setGenerando(false);
      setError(
        e instanceof GenerateAdError && e.isActionable
          ? e.message
          : 'No se pudo generar el carrusel. Intentá nuevamente.',
      );
    }
  };

  // ── Piezas compartidas entre mobile y desktop ────────────────────────────

  const heroImg = (className: string) => (
    <img
      src={HERO_IMG}
      alt="Ejemplo del modo Carrusel"
      className={className}
    />
  );

  const errorBox = error ? (
    <div className="mt-3 rounded-lg bg-error-soft text-error-text text-[12.5px] px-3 py-2">
      {error}
    </div>
  ) : null;

  /**
   * MOBILE, paso 1: la anatomía del modo Modelo —
   * ADR-2026-09-08-todos-los-modos-abren-igual-en-mobile. Título, bajada, carátula y UNA
   * card al pie, en vez del panel blanco con "Subir imagen" + "Elegir de mis generadas".
   *
   * "Subir imagen" no se pierde: pasó adentro del sheet que abre la card, como primera
   * celda de la grilla — el mismo lugar que ya ocupa en el panel de escritorio.
   */
  const pantallaMobileComoModelo = (
    <PasoUnoMobile
      titulo="Elegí la imagen del carrusel"
      bajada="Atlas va a generar nuevas tomas del mismo producto, en la misma escena."
      portada={heroImg('max-w-[400px] max-h-full w-full h-auto object-contain')}
      Icono={Images}
      cardTexto="Elegí tu imagen"
      onAbrir={() => setSheetGeneradas(true)}
      aviso={errorBox}
    />
  );

  /** Opciones del carrusel (pantalla 2). Igual en las dos variantes. */
  const bloqueConfigurar = (
    <>
      <p className="text-[15px] text-brand-black leading-snug mb-5">
        Se generarán{' '}
        <span className="font-semibold">{cantidad} imágenes nuevas</span> a partir de
        esta foto, con distintos ángulos y variaciones.
      </p>

      <p className="text-[13px] text-[#3C3C43]/60 uppercase tracking-wide mb-2">
        Cantidad
      </p>
      <div className="rounded-[13px] bg-white overflow-hidden border border-neutral-200">
        <div className="px-4 py-[11px]">
          <div className="flex items-center justify-between">
            <span className="text-[15px] text-brand-black">Imágenes a generar</span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setCantidad((n) => Math.max(CANTIDAD_MIN, n - 1))}
                disabled={cantidad <= CANTIDAD_MIN}
                aria-label="Menos"
                className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-neutral-100 text-brand-black hover:bg-neutral-200 active:bg-neutral-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="text-[16px] font-semibold text-brand-black w-5 text-center tabular-nums">
                {cantidad}
              </span>
              <button
                type="button"
                onClick={() => setCantidad((n) => Math.min(CANTIDAD_MAX, n + 1))}
                disabled={cantidad >= CANTIDAD_MAX}
                aria-label="Más"
                className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-neutral-100 text-brand-black hover:bg-neutral-200 active:bg-neutral-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <Info className="w-3.5 h-3.5 text-brand-primary shrink-0" />
            <span className="text-[13px] text-[#3C3C43]/60">
              Se consumirán {costo} imágenes
            </span>
          </div>
        </div>
      </div>
      {errorBox}
    </>
  );

  /** El botón Generar. Lo comparten los dos pies —el de mobile y el del panel—: lo
   *  único que cambia entre ellos es el aire alrededor. */
  const botonGenerar = (
    <button
      type="button"
      onClick={generar}
      disabled={generando}
      className="w-full inline-flex items-center justify-center gap-2 py-3.5 px-6 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-[15.5px] font-semibold shadow-sm transition-colors"
    >
      <Sparkles className="w-5 h-5" />
      Generar carrusel
      <CreditBadge credits={costo} tone="on-primary" size="sm" />
    </button>
  );

  /** Pie de MOBILE: sticky al fondo del panel blanco de abajo. */
  const pieGenerarMobile = (
    <div className="shrink-0 bg-white px-5 pt-3 pb-5 border-t border-black/[0.06]">
      {botonGenerar}
    </div>
  );

  /** Pie del PANEL de desktop — el mismo que el de los otros modos del rediseño. */
  const pieGenerarPanel = (
    <div className="shrink-0 px-6 pt-3 pb-4 bg-white border-t border-black/[0.05]">
      {botonGenerar}
    </div>
  );

  // ── Piezas del DESKTOP: el diseño de Automática / Modelo / Catálogo ───────

  /**
   * "Subir imagen" como PRIMERA CELDA de la grilla, no como botón arriba de ella: es
   * el lugar que ocupa "Subir nuevo producto" en la grilla de productos de los otros
   * modos, y por el mismo motivo — subir una foto y elegir una generada responden la
   * MISMA pregunta.
   *
   * El chip del ícono va en hex con alpha y no en `bg-brand-primary/10`: el token no
   * tiene `<alpha-value>` y el modificador no emite ninguna regla.
   */
  const celdaSubirImagen = (
    <button
      type="button"
      onClick={elegirArchivo}
      disabled={subiendo}
      className="relative aspect-square rounded-[13px] overflow-hidden bg-white border-2 border-dashed border-neutral-300 hover:border-brand-primary hover:bg-[#1644EB]/[0.03] disabled:opacity-60 disabled:cursor-not-allowed active:scale-[0.98] transition-all flex flex-col items-center justify-center gap-2 px-2"
    >
      <span className="w-9 h-9 rounded-xl bg-[#1644EB]/10 flex items-center justify-center">
        {subiendo ? (
          <Loader2 className="w-5 h-5 text-brand-primary animate-spin" />
        ) : (
          <ImageUp className="w-5 h-5 text-brand-primary" />
        )}
      </span>
      <span className="text-[11.5px] font-bold text-brand-primary text-center leading-tight">
        {subiendo ? 'Subiendo…' : 'Subir imagen'}
      </span>
    </button>
  );

  /**
   * La grilla del modo: subir + las generadas. La MISMA en los dos lugares donde se
   * elige la imagen —el paso 1 y el panel lateral del paso 2—, que es lo que hace que
   * cambiarla sea el mismo gesto que elegirla.
   */
  const grillaDeImagenes = (
    <GeneradasPickGrid
      companyId={companyId}
      // En el paso 2 la que está en uso va tildada: el panel se abre para cambiarla.
      selectedId={source?.tipo === 'generada' ? source.imageId : null}
      onSelect={elegirGenerada}
      // Con el flag de rollback a n8n no hay forma de partir de una foto suelta: ahí
      // la celda de subir no va y queda sólo el camino de las generadas.
      tileInicial={ANGLES_SUBIDA_REQUIERE_ATLAS ? undefined : celdaSubirImagen}
      emptyHint="Todavía no generaste ninguna imagen. Subí una foto para armar el carrusel."
    />
  );

  /** Paso 1 en el panel: el set de imágenes del que sale la del carrusel. */
  const panelElegir = (
    <>
      {/* UNA sola línea, en la misma medida que la de los otros modos ("Elegí uno o
          varios productos"): el título y su bajada decían dos veces lo mismo y entre
          los dos empujaban la grilla para abajo. */}
      <div className="shrink-0 px-6 flex justify-between gap-2 items-baseline pt-5 pb-1">
        <p className="text-[17px] font-semibold text-brand-black leading-tight">
          Elegí la imagen de la que Atlas va a sacar nuevas tomas
        </p>
      </div>
      <div className="relative flex-1 min-h-0 flex flex-col">
        <div
          ref={scrollElegirRef}
          onScroll={(e) => medirFinDelScroll(e.currentTarget)}
          className="flex-1 min-h-0 overflow-y-auto px-6 pt-4"
        >
          <div ref={contenidoElegirRef}>
            {grillaDeImagenes}
            {errorBox}
            <div className="h-3" />
          </div>
        </div>
        {/* Degradado del pie: la última fila se desvanece en vez de cortarse contra el
            borde. Se apaga al llegar abajo — si quedara siempre, dejaría de significar
            "hay más". */}
        {quedaContenidoAbajo && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-white via-white/85 to-transparent" />
        )}
      </div>
    </>
  );

  /** Paso 2 en el panel: la imagen elegida, cuántas variaciones y el Generar. */
  const panelConfigurar = (
    <>
      {/* La imagen elegida encabeza la configuración con la MISMA card con la que Fotos
          de producto y Catálogo encabezan la suya con los productos. Tocarla abre el
          panel lateral: cambiar de imagen ya no es volver al paso 1. */}
      <div className="shrink-0 px-6 pt-5 pb-1">
        <CardPicker
          etiqueta="Imagen"
          valor={
            source?.tipo === 'subida' ? 'Foto que subiste' : 'De tus creaciones'
          }
          imagenes={previewUrl ? [previewUrl] : []}
          onClick={() => setPanelImagenAbierto(true)}
        />
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto px-6 pt-3 pb-4">
        {bloqueConfigurar}
      </div>
      {pieGenerarPanel}
    </>
  );

  const overlays = (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onFile}
      />
      <OptionSheet
        open={sheetGeneradas}
        onClose={() => setSheetGeneradas(false)}
        title="Mis imágenes generadas"
        hideAccept
      >
        {/* La MISMA grilla del panel de escritorio: "Subir imagen" es su primera celda,
            que es donde vive ese camino desde que la pantalla dejó de tener dos CTA. */}
        {grillaDeImagenes}
      </OptionSheet>
      {showLoading && (
        <LoadingAnimation
          message="Generando carrusel…"
          subMessage="Esto puede tardar unos minutos."
          durationMs={CARGA_MS}
          completeNow={completeNow}
          onCompleted={() => {
            setShowLoading(false);
            setGenerando(false);
            const pend = pendingNavRef.current;
            pendingNavRef.current = null;
            if (pend) {
              navigate('/redes-sociales/imagenes-generadas', {
                state: {
                  imageIds: pend.imageIds,
                  companyId: pend.companyId,
                  preloadedImages: pend.preloaded,
                },
              });
            }
          }}
        />
      )}
      <SubscriptionLimitModal
        isOpen={limitModal !== null}
        type={limitModal ?? 'plan-limit-reached'}
        onClose={() => setLimitModal(null)}
      />
    </>
  );

  const enConfigurar = paso === 'configurar' && !!source && !!previewUrl;

  return (
    <>
      {/* ── MOBILE ────────────────────────────────────────────────────────── */}
      {!enConfigurar ? (
        pantallaMobileComoModelo
      ) : (
        /* PASO 2: la imagen elegida a sangre y el panel blanco con la configuración. Ese
           paso no cambió — lo que se mira ahí es la foto, no una lista. */
        <div className="md:hidden fixed inset-0 z-30 bg-surface-mobile flex flex-col">
          <div className="relative flex-1 min-h-0 bg-neutral-100">
            <CachedImg
              src={previewUrl as string}
              alt=""
              className="absolute inset-0 w-full h-full object-contain"
            />
          </div>
          <div
            className="relative z-10 bg-white shrink-0 -mt-6 rounded-t-3xl flex flex-col overflow-hidden"
            style={{ maxHeight: '70dvh' }}
          >
            <div className="flex-1 min-h-0 overflow-y-auto px-5 pt-7 pb-6">
              {bloqueConfigurar}
            </div>
            {pieGenerarMobile}
          </div>
        </div>
      )}

      {/* ── DESKTOP: el diseño de los modos — panel a la izquierda, muestra a la
          derecha (ADR-2026-09-07-carrusel-toma-el-diseno-de-los-modos) ───────── */}
      <ShellModoRedisenado
        sidebarWidth={sidebarWidth}
        // Los mismos dos niveles que arman los otros modos: la home y el modo. El paso
        // no se suma como tercer nivel — la cabecera del panel no cambia de paso.
        migas={[
          { label: 'Inicio', onClick: () => navigate('/interfaz-v2') },
          { label: nombreCortoModo('carrusel', 'Carrusel de imágenes') },
        ]}
        titulo={nombreCortoModo('carrusel', 'Carrusel de imágenes')}
        panel={enConfigurar ? panelConfigurar : panelElegir}
        // Con resultados en la columna, ésta se sale de los paddings y llega al marco —
        // el mismo tratamiento que en los modos de `InterfazV2`
        // (ADR-2026-09-06-la-columna-de-resultados-llega-al-marco).
        muestraAlBorde={hayColumna}
        // El visor de una creación es un lightbox portaleado al body: sin bajarle el z a
        // este panel queda debajo y sólo se ve su velo.
        cederZIndex={visorAbierto}
        muestra={
          columna ?? (
          <div
            // `key` por paso: la columna se remonta y hace su entrada, igual que en los
            // otros modos al cambiar de paso.
            key={enConfigurar ? 'muestra-elegida' : 'muestra-hero'}
            className="flex-1 min-h-0 flex flex-col items-center justify-center gap-2 animate-paso-adelante"
          >
            {enConfigurar ? (
              // Sin el badge "Imagen seleccionada" que llevaba antes: ahora quien dice
              // cuál es la imagen elegida —y que se puede cambiar— es la card del panel
              // de al lado, así que el badge repetía el dato encima de la foto.
              <CachedImg
                src={previewUrl as string}
                alt=""
                className="max-w-[560px] max-h-[480px] w-auto h-auto object-contain rounded-2xl"
              />
            ) : (
              <>
                {/* El texto ENCABEZA la muestra en vez de ir de pie de foto — el mismo
                    tratamiento de Modelo v2 y Catálogo
                    (ADR-2026-08-21-modelo-v2-la-muestra-se-queda): título en negrita
                    arriba y la bajada debajo, para que se lea como el título de lo que
                    el modo hace y no como el epígrafe de la imagen. */}
                <div className="shrink-0 text-center max-w-[440px] mb-3">
                  <h2 className="text-[19px] font-bold leading-tight tracking-[-0.01em] text-brand-black/90">
                    {HERO_TITULO}
                  </h2>
                  <p className="text-[15.5px] text-[#3C3C43]/60 leading-relaxed mt-1">
                    {HERO_DESC}
                  </p>
                </div>
                {heroImg(
                  'max-w-[560px] max-h-[480px] w-auto h-auto object-contain rounded-2xl',
                )}
              </>
            )}
          </div>
          )
        }
        // Cambiar la imagen sin salir de la configuración: la misma capa con la que
        // Fotos de producto y Catálogo cambian sus productos. Sólo en el paso 2 — en el
        // 1 la grilla ya ES la pantalla.
        panelLateral={({ contenedorRef, offsetIzquierdo }) => (
          <PanelLateralModo
            abierto={enConfigurar && panelImagenAbierto}
            onCerrar={() => setPanelImagenAbierto(false)}
            titulo="Elegí la imagen"
            offsetIzquierdo={offsetIzquierdo}
            contenedorRef={contenedorRef}
          >
            <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 pt-2">
              <p className="text-[13.5px] text-[#3C3C43]/60 leading-relaxed pb-3">
                Subí una foto nueva o elegí otra de tus creaciones. El panel se cierra al
                elegir.
              </p>
              {grillaDeImagenes}
              {errorBox}
            </div>
          </PanelLateralModo>
        )}
      />

      {overlays}
    </>
  );
}
